import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Slider,
  Typography,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Close,
  PowerSettingsNew,
  SettingsInputAntenna,
  Refresh,
  FlashOn,
  TrendingUp,
  Info,
  Warning,
  CheckCircle,
  Speed,
} from "@mui/icons-material";
import { ReaderDialogHeader, readerDialogPaper } from "./DeviceActionDialog";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import PropTypes from "prop-types";

const SetPowerDialog = ({
  open,
  onClose,
  onConfirm,
  onRefreshPower,
  antenna,
  reader,
  gettingPower = {},
  settingPower = {},
}) => {
  // Estados
  const [powerValue, setPowerValue] = useState(2800); // Valor en mW para Zebra, en dBm para Hura
  const [customValue, setCustomValue] = useState("28.0");
  const [error, setError] = useState("");
  const [currentPower, setCurrentPower] = useState(null);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Determinar el tipo de reader (Zebra o Hura)
  const readerType = reader?.reader_type?.toLowerCase() || "zebra"; // Por defecto Zebra
  const isHura = readerType === "hura";

  // Configuración según tipo de reader
  const powerConfig = isHura
    ? {
        min: 5, // 5 dBm
        max: 30, // 30 dBm
        step: 1, // Enteros
        default: 15, // Valor por defecto 15 dBm
        unit: "dBm",
        description: "Potencia configurable de 5 a 30 dBm (números enteros)",
        // Para Hura, powerValue se maneja en dBm directamente
        valueToSend: (val) => val, // Enviar el valor directamente en dBm
        mwToDisplay: (val) => val, // El valor ya está en dBm
        dbmToValue: (dbm) => dbm, // Convertir dBm a valor interno (mismo valor)
      }
    : {
        min: 10.0, // 10.0 dBm
        max: 30.0, // 30.0 dBm
        step: 0.1, // Decimales
        default: 28.0, // Valor por defecto 28.0 dBm
        unit: "dBm",
        description: "Potencia configurable de 10.0 a 30.0 dBm (incrementos de 0.1 dBm)",
        // Para Zebra, powerValue se maneja en mW internamente
        valueToSend: (val) => val, // El valor ya está en mW
        mwToDisplay: (val) => val, // El valor ya está en mW
        dbmToValue: (dbm) => Math.round(dbm * 100), // Convertir dBm a mW
      };

  // Referencias para controlar efectos
  const hasLoadedInitialPower = useRef(false);
  const isMounted = useRef(true);

  // Potencias predefinidas según tipo de reader
  const getPredefinedPowers = () => {
    if (isHura) {
      return [
        { label: "Mínima", value: 5, description: "5 dBm - Alcance mínimo", level: "low" },
        { label: "Baja", value: 10, description: "10 dBm - Alcance bajo", level: "low" },
        {
          label: "Media-Baja",
          value: 15,
          description: "15 dBm - Alcance moderado",
          level: "medium-low",
        },
        { label: "Media", value: 20, description: "20 dBm - Alcance estándar", level: "medium" },
        {
          label: "Media-Alta",
          value: 25,
          description: "25 dBm - Buen alcance",
          level: "medium-high",
        },
        { label: "Alta", value: 28, description: "28 dBm - Alcance óptimo", level: "high" },
        { label: "Máxima", value: 30, description: "30 dBm - Alcance máximo", level: "max" },
      ];
    } else {
      return [
        { label: "Mínima", value: 10.0, description: "10.0 dBm - Alcance mínimo", level: "low" },
        {
          label: "Media-Baja",
          value: 15.0,
          description: "15.0 dBm - Alcance moderado",
          level: "medium-low",
        },
        {
          label: "Media",
          value: 20.0,
          description: "20.0 dBm - Alcance estándar",
          level: "medium",
        },
        {
          label: "Media-Alta",
          value: 25.0,
          description: "25.0 dBm - Buen alcance",
          level: "medium-high",
        },
        { label: "Alta", value: 28.0, description: "28.0 dBm - Alcance óptimo", level: "high" },
        { label: "Máxima", value: 30.0, description: "30.0 dBm - Alcance máximo", level: "max" },
      ];
    }
  };

  const predefinedPowers = getPredefinedPowers();

  // Efecto de limpieza al desmontar
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Establecer valor por defecto según tipo de reader
  useEffect(() => {
    if (!open) return;

    // Establecer valor por defecto según el tipo
    if (isHura) {
      // Para Hura: el valor interno es directamente en dBm
      setPowerValue(powerConfig.default);
      setCustomValue(powerConfig.default.toString());
    } else {
      // Para Zebra: convertir de dBm a mW
      const defaultMW = convertDBmToMW(powerConfig.default);
      setPowerValue(defaultMW);
      setCustomValue(powerConfig.default.toString());
    }
  }, [open, isHura]);

  // Cargar potencia actual al abrir - SOLO UNA VEZ
  useEffect(() => {
    // Resetear flag cuando se cierra el diálogo
    if (!open) {
      hasLoadedInitialPower.current = false;
      return;
    }

    // Solo cargar si está abierto, tiene antena, y no hemos cargado antes
    if (open && antenna?.id && onRefreshPower && !hasLoadedInitialPower.current) {
      hasLoadedInitialPower.current = true;
      loadCurrentPower();
      setIsSuccess(false);
      setSuccessMessage("");
      setError("");
    }
  }, [open, antenna?.id, onRefreshPower]);

  // Resetear estados al cerrar
  useEffect(() => {
    if (!open) {
      if (isHura) {
        setPowerValue(powerConfig.default);
        setCustomValue(powerConfig.default.toString());
      } else {
        const defaultMW = convertDBmToMW(powerConfig.default);
        setPowerValue(defaultMW);
        setCustomValue(powerConfig.default.toString());
      }
      setError("");
      setCurrentPower(null);
      setIsSuccess(false);
      setSuccessMessage("");
    }
  }, [open, isHura]);

  // Función para cargar la potencia actual
  const loadCurrentPower = async () => {
    if (!antenna?.id || !onRefreshPower) {
      console.error("❌ No se puede cargar potencia: falta antena o función");
      setError("No se puede cargar la potencia actual");
      return;
    }

    // Verificar si ya estamos cargando
    if (isLoadingCurrent) {
      return;
    }

    setIsLoadingCurrent(true);
    setError("");

    try {
      const result = await onRefreshPower(antenna.id);

      if (!isMounted.current) return;

      if (result?.success) {
        setCurrentPower(result);

        // Actualizar valores con la potencia obtenida
        if (result.power !== undefined && result.power !== null) {
          if (isHura) {
            // Para Hura: el power viene en dBm
            setPowerValue(result.power);
            setCustomValue(result.power.toString());
          } else {
            // Para Zebra: el power viene en mW
            setPowerValue(result.power);
            const dbmValue = (result.power / 100).toFixed(1);
            setCustomValue(dbmValue);
          }
        } else if (result.formattedPower) {
          // Si solo viene formattedPower (en dBm)
          const dbmValue = parseFloat(result.formattedPower);
          if (isHura) {
            setPowerValue(dbmValue);
            setCustomValue(dbmValue.toString());
          } else {
            const mwValue = Math.round(dbmValue * 100);
            setPowerValue(mwValue);
            setCustomValue(dbmValue.toString());
          }
        }
      } else {
        setError(
          `No se pudo obtener potencia: ${result?.error || result?.message || "Error desconocido"}`
        );
      }
    } catch (err) {
      if (!isMounted.current) return;
      console.error("❌ Error en loadCurrentPower:", err);
      setError(`Error al cargar potencia: ${err.message}`);
    } finally {
      if (isMounted.current) {
        setIsLoadingCurrent(false);
      }
    }
  };

  // Funciones de conversión
  const formatMWToDBm = (mW) => {
    if (!mW) return "0.0";
    const dbm = mW / 100;
    return dbm.toFixed(1);
  };

  const convertDBmToMW = (dbm) => {
    if (!dbm) return 0;
    const numericValue = parseFloat(dbm);
    if (isNaN(numericValue)) return 0;
    return Math.round(numericValue * 100);
  };

  // Obtener el valor a mostrar en dBm
  const getDisplayDBm = () => {
    if (isHura) {
      return powerValue.toString();
    } else {
      return (powerValue / 100).toFixed(1);
    }
  };

  // Obtener el valor equivalente en mW (solo para mostrar)
  const getEquivalentMW = () => {
    if (isHura) {
      return powerValue * 100; // Convertir dBm a mW para mostrar
    } else {
      return powerValue; // Ya está en mW
    }
  };

  // Manejadores de eventos
  const handleSliderChange = (event, newValue) => {
    setPowerValue(newValue);
    if (isHura) {
      setCustomValue(newValue.toString());
    } else {
      const dbmValue = (newValue / 100).toFixed(1);
      setCustomValue(dbmValue);
    }
    setError("");
  };

  const handleCustomChange = (event) => {
    const value = event.target.value;
    setCustomValue(value);

    // Validar formato
    if (!value.match(/^[0-9]*\.?[0-9]*$/)) {
      setError("Solo números y punto decimal permitidos");
      return;
    }

    if (value === "") {
      setError("");
      return;
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      setError("Valor no válido");
      return;
    }

    // Validar que sea entero para Hura
    if (isHura && !Number.isInteger(numericValue)) {
      setError(`Para readers Hura la potencia debe ser un número entero (5-30)`);
      return;
    }

    // Validar rango
    if (numericValue < powerConfig.min) {
      setError(`Potencia mínima: ${powerConfig.min} dBm`);
      return;
    }

    if (numericValue > powerConfig.max) {
      setError(`Potencia máxima: ${powerConfig.max} dBm`);
      return;
    }

    // Validar decimales (solo para Zebra)
    if (!isHura) {
      const decimalPlaces = (value.split(".")[1] || "").length;
      if (decimalPlaces > 1) {
        setError("Máximo 1 decimal (ej: 28.0, 28.5)");
        return;
      }
    }

    setError("");

    // Actualizar powerValue según el tipo
    if (isHura) {
      setPowerValue(numericValue);
    } else {
      setPowerValue(convertDBmToMW(numericValue));
    }
  };

  const handlePredefinedSelect = (dbmValue) => {
    setCustomValue(dbmValue.toString());
    if (isHura) {
      setPowerValue(dbmValue);
    } else {
      setPowerValue(convertDBmToMW(dbmValue));
    }
    setError("");
  };

  const handleConfirm = async () => {
    if (error || !antenna?.id) {
      setError("No se puede configurar: datos incompletos");
      return;
    }

    // Validar rango final
    const dbmValue = parseFloat(customValue);
    if (dbmValue < powerConfig.min || dbmValue > powerConfig.max) {
      setError(`Potencia fuera de rango (${powerConfig.min}-${powerConfig.max} dBm)`);
      return;
    }

    // Validar que sea entero para Hura
    if (isHura && !Number.isInteger(dbmValue)) {
      setError(`Para readers Hura la potencia debe ser un número entero`);
      return;
    }

    try {
      // Para Hura: enviar el valor directamente en dBm
      // Para Zebra: enviar en mW
      const valueToSend = isHura ? dbmValue : powerValue;

      const result = await onConfirm(antenna.id, valueToSend, readerType);

      if (result?.success) {
        setIsSuccess(true);
        setSuccessMessage(result.message || "Potencia configurada exitosamente");

        // Actualizar potencia actual mostrada
        if (result.power) {
          setCurrentPower({
            power: result.power,
            formattedPower:
              result.formattedPower ||
              (isHura ? result.power.toString() : formatMWToDBm(result.power)),
            message: result.message,
            timestamp: new Date().toISOString(),
          });
        }

        // Cerrar automáticamente después de 2 segundos
        setTimeout(() => {
          if (isMounted.current) {
            onClose();
          }
        }, 2000);
      } else {
        setError(`Error del servidor: ${result?.message || "Configuración falló"}`);
      }
    } catch (err) {
      console.error("❌ Error en handleConfirm:", err);
      setError(`Error: ${err.message}`);
    }
  };

  const getPowerLevelDescription = (value) => {
    let dbm;
    if (isHura) {
      dbm = value; // El valor ya está en dBm
    } else {
      dbm = value / 100; // Convertir de mW a dBm
    }

    if (isHura) {
      if (dbm < 10) return "Potencia muy baja - Alcance mínimo";
      if (dbm < 15) return "Potencia baja - Alcance limitado";
      if (dbm < 20) return "Potencia media-baja - Alcance moderado";
      if (dbm < 25) return "Potencia media - Alcance estándar";
      if (dbm < 28) return "Potencia media-alta - Buen alcance";
      if (dbm < 30) return "Potencia alta - Alcance óptimo";
      return "Potencia máxima - Alcance extendido";
    } else {
      if (dbm < 15) return "Potencia baja - Alcance reducido";
      if (dbm < 20) return "Potencia media-baja - Alcance moderado";
      if (dbm < 25) return "Potencia media - Alcance estándar";
      if (dbm < 28) return "Potencia alta - Buen alcance";
      if (dbm < 30) return "Potencia muy alta - Alcance óptimo";
      return "Potencia máxima - Alcance extendido";
    }
  };

  const getPowerLevelColor = (value) => {
    let dbm;
    if (isHura) {
      dbm = value;
    } else {
      dbm = value / 100;
    }

    if (dbm < 15) return "#ff9800";
    if (dbm < 20) return "#ffb74d";
    if (dbm < 25) return "#4caf50";
    if (dbm < 28) return "#2e7d32";
    if (dbm < 30) return "#1b5e20";
    return "#d50000";
  };

  if (!antenna) return null;

  const isProcessing = settingPower[antenna.id] || false;
  const displayDBm = getDisplayDBm();
  const equivalentMW = getEquivalentMW();
  const powerLevelColor = getPowerLevelColor(powerValue);

  return (
    <Dialog
      open={open}
      onClose={isProcessing || isSuccess ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: readerDialogPaper }}
    >
      {/* Encabezado */}
      <ReaderDialogHeader
        title="Configurar potencia"
        subtitle={`${
          antenna.rawData?.namepoint ||
          antenna.name ||
          antenna.rawData?.point ||
          `Antena ${antenna.id}`
        } · ${reader?.hostname || "Reader seleccionado"}`}
        onClose={onClose}
        busy={isProcessing}
      />

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f9fc" }}>
        {isSuccess ? (
          // Vista de éxito
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "success.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: "success.main" }} />
            </Box>

            <MDTypography variant="h5" fontWeight="bold" color="success" gutterBottom>
              Potencia Configurada Exitosamente
            </MDTypography>

            <MDTypography variant="body1" color="text.secondary" paragraph>
              {successMessage}
            </MDTypography>

            <Box
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "success.light",
                bgcolor: "success.50",
                maxWidth: 400,
                mx: "auto",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="secondary" display="block">
                    Antena
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">
                    {antenna.rawData?.namepoint || antenna.rawData?.point || `Antena ${antenna.id}`}
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="secondary" display="block">
                    Potencia Configurada
                  </MDTypography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={`${displayDBm} dBm`}
                      color="success"
                      size="small"
                      sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      ({equivalentMW} mW)
                    </Typography>
                  </Box>
                </Grid>
                {reader && (
                  <>
                    <Grid item xs={6}>
                      <MDTypography variant="caption" color="secondary" display="block">
                        Reader
                      </MDTypography>
                      <MDTypography variant="body1" fontWeight="medium">
                        {reader.hostname}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="caption" color="secondary" display="block">
                        Tipo Reader
                      </MDTypography>
                      <MDTypography variant="body1" fontWeight="medium">
                        {reader.reader_type || "Zebra"}
                      </MDTypography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <MDTypography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
              Este diálogo se cerrará automáticamente...
            </MDTypography>
          </Box>
        ) : (
          // Vista de configuración
          <Box>
            {/* Información de la antena */}
            <Box
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "primary.light",
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <SettingsInputAntenna sx={{ fontSize: 24, color: "primary.main" }} />
                    <Box>
                      <MDTypography variant="h6" fontWeight="bold">
                        {antenna.rawData?.namepoint ||
                          antenna.rawData?.point ||
                          `Antena ${antenna.id}`}
                      </MDTypography>
                      <MDTypography variant="caption" color="secondary">
                        ID: {antenna.id} •{" "}
                        {reader?.hostname ? `Reader: ${reader.hostname}` : "Sin reader asociado"} •
                        Tipo: {reader?.reader_type || "Zebra"}
                      </MDTypography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={2}>
                    <Box>
                      <MDTypography variant="caption" color="secondary" display="block">
                        Potencia actual
                      </MDTypography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {isLoadingCurrent ? (
                          <CircularProgress size={20} />
                        ) : currentPower ? (
                          <>
                            <Chip
                              label={`${
                                currentPower.formattedPower ||
                                (isHura ? currentPower.power : formatMWToDBm(currentPower.power))
                              } dBm`}
                              color="primary"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                height: 32,
                              }}
                              icon={<Speed fontSize="small" />}
                            />
                            <Tooltip title="Actualizar lectura">
                              <IconButton
                                size="small"
                                onClick={loadCurrentPower}
                                disabled={isLoadingCurrent || isProcessing}
                              >
                                <Refresh fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <MDTypography variant="body2" color="text.disabled">
                            Sin datos de potencia
                          </MDTypography>
                        )}
                      </Box>
                    </Box>

                    {currentPower && (
                      <Box>
                        <MDTypography variant="caption" color="secondary" display="block">
                          Nivel
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium">
                          {getPowerLevelDescription(currentPower.power || 0)}
                        </MDTypography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <MDTypography variant="caption" color="secondary" display="block" gutterBottom>
                      Rango permitido
                    </MDTypography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" fontWeight="bold">
                        {powerConfig.min} dBm
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        a
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {powerConfig.max} dBm
                      </Typography>
                    </Box>
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {isHura ? "Números enteros" : `Incrementos de ${powerConfig.step} dBm`}
                    </MDTypography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Nivel de potencia seleccionado */}
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                border: 1,
                borderColor: `${powerLevelColor}40`,
                bgcolor: `${powerLevelColor}10`,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <FlashOn sx={{ color: powerLevelColor }} />
                  <MDTypography variant="subtitle1" fontWeight="bold">
                    Potencia seleccionada
                  </MDTypography>
                </Box>
                <Chip
                  label={`${displayDBm} dBm`}
                  sx={{
                    bgcolor: powerLevelColor,
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    height: 32,
                  }}
                />
              </Box>

              <MDTypography variant="body2" color="text.secondary">
                {getPowerLevelDescription(powerValue)} • Equivalente a {equivalentMW} mW
              </MDTypography>
            </Box>

            {/* Control deslizante */}
            <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <MDTypography variant="subtitle1" fontWeight="bold">
                  <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
                  Ajuste de potencia
                </MDTypography>
                <MDTypography variant="caption" color="secondary">
                  Deslice para ajustar
                </MDTypography>
              </Box>

              <Box sx={{ px: 1 }}>
                <Slider
                  value={powerValue}
                  onChange={handleSliderChange}
                  min={isHura ? powerConfig.min : convertDBmToMW(powerConfig.min)}
                  max={isHura ? powerConfig.max : convertDBmToMW(powerConfig.max)}
                  step={isHura ? powerConfig.step : convertDBmToMW(powerConfig.step)}
                  marks={
                    isHura
                      ? [
                          { value: 5, label: "5" },
                          { value: 10, label: "10" },
                          { value: 15, label: "15" },
                          { value: 20, label: "20" },
                          { value: 25, label: "25" },
                          { value: 30, label: "30" },
                        ]
                      : [
                          { value: convertDBmToMW(10.0), label: "10.0" },
                          { value: convertDBmToMW(15.0), label: "15.0" },
                          { value: convertDBmToMW(20.0), label: "20.0" },
                          { value: convertDBmToMW(25.0), label: "25.0" },
                          { value: convertDBmToMW(30.0), label: "30.0" },
                        ]
                  }
                  disabled={isProcessing}
                  sx={{
                    color: powerLevelColor,
                    height: 8,
                    "& .MuiSlider-thumb": {
                      height: 24,
                      width: 24,
                      backgroundColor: "#fff",
                      border: `2px solid ${powerLevelColor}`,
                      "&:hover": {
                        boxShadow: `0 0 0 8px ${powerLevelColor}20`,
                      },
                    },
                    "& .MuiSlider-markLabel": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  Mínimo ({powerConfig.min} dBm)
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  Máximo ({powerConfig.max} dBm)
                </Typography>
              </Box>
            </Box>

            {/* Valor personalizado */}
            <Box sx={{ mb: 4 }}>
              <MDTypography variant="subtitle1" fontWeight="bold" gutterBottom>
                Valor específico
              </MDTypography>

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    value={customValue}
                    onChange={handleCustomChange}
                    placeholder={isHura ? "Ej: 15" : "Ej: 28.0"}
                    label="Potencia en dBm"
                    error={!!error}
                    helperText={
                      error ||
                      `Ingrese un valor entre ${powerConfig.min} y ${powerConfig.max} dBm${
                        isHura ? " (entero)" : ""
                      }`
                    }
                    disabled={isProcessing}
                    InputProps={{
                      startAdornment: <Speed sx={{ mr: 1, color: "action.active" }} />,
                      endAdornment: (
                        <Typography variant="caption" color="text.secondary">
                          dBm
                        </Typography>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "1.1rem",
                        fontWeight: "medium",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="secondary" display="block">
                      Equivalente en mW
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {equivalentMW} mW
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Potencias predefinidas */}
            <Box sx={{ mb: 4 }}>
              <MDTypography variant="subtitle1" fontWeight="bold" gutterBottom>
                <FlashOn sx={{ mr: 1, verticalAlign: "middle" }} />
                Configuraciones rápidas
              </MDTypography>

              <Grid container spacing={2}>
                {predefinedPowers.map((item, index) => {
                  const isSelected = isHura
                    ? powerValue === item.value
                    : powerValue === convertDBmToMW(item.value);

                  return (
                    <Grid item xs={6} md={4} key={index}>
                      <MDButton
                        fullWidth
                        variant={isSelected ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handlePredefinedSelect(item.value)}
                        disabled={isProcessing}
                        sx={{
                          py: 2,
                          borderRadius: 2,
                          justifyContent: "flex-start",
                          height: "auto",
                          minHeight: 80,
                        }}
                      >
                        <Box textAlign="left" width="100%">
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.5}
                          >
                            <MDTypography variant="body2" fontWeight="bold">
                              {item.label}
                            </MDTypography>
                            <Chip
                              label={`${item.value} dBm`}
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                              }}
                            />
                          </Box>
                          <MDTypography variant="caption" color="text.secondary" display="block">
                            {item.description}
                          </MDTypography>
                        </Box>
                      </MDButton>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Advertencias específicas por tipo */}
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{
                mb: 2,
                borderRadius: 2,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              <Box>
                <MDTypography variant="body2" fontWeight="bold" gutterBottom>
                  Consideraciones importantes para {isHura ? "Hura" : "Zebra"}
                </MDTypography>
                <MDTypography variant="caption" component="div">
                  {isHura ? (
                    <>
                      1. La potencia debe ser un número entero entre 5 y 30 dBm
                      <br />
                      2. Valores superiores a 25 dBm pueden afectar la vida útil del equipo
                      <br />
                      3. El cambio puede tardar varios segundos en aplicarse
                      <br />
                      4. Verifique que la configuración sea compatible con su hardware Hura
                    </>
                  ) : (
                    <>
                      1. La potencia se configura en incrementos de 0.1 dBm (10 mW)
                      <br />
                      2. Valores mayores a 28.0 dBm pueden afectar la vida útil de la antena
                      <br />
                      3. El cambio puede tardar varios segundos en aplicarse
                      <br />
                      4. Verifique que el rango sea compatible con su hardware Zebra
                    </>
                  )}
                </MDTypography>
              </Box>
            </Alert>

            {/* Mostrar error si existe */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                <MDTypography variant="body2">{error}</MDTypography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Acciones */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isSuccess && (
          <>
            <MDButton
              onClick={onClose}
              variant="outlined"
              color="secondary"
              disabled={isProcessing}
              sx={{
                minWidth: 120,
                borderRadius: 1,
                fontWeight: "medium",
              }}
            >
              Cancelar
            </MDButton>

            <Box sx={{ flexGrow: 1 }} />

            <MDButton
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              startIcon={
                isProcessing ? <CircularProgress size={20} color="inherit" /> : <PowerSettingsNew />
              }
              disabled={isProcessing || !!error || !customValue}
              sx={{
                minWidth: 180,
                borderRadius: 1,
                fontWeight: "bold",
                fontSize: "0.9rem",
                py: 1,
              }}
            >
              {isProcessing ? "CONFIGURANDO..." : "Guardar potencia"}
            </MDButton>
          </>
        )}

        {isSuccess && (
          <MDButton
            onClick={onClose}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            sx={{
              minWidth: 180,
              borderRadius: 1,
              fontWeight: "bold",
            }}
          >
            CERRAR
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

SetPowerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onRefreshPower: PropTypes.func.isRequired,
  antenna: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    rawData: PropTypes.shape({
      namepoint: PropTypes.string,
      point: PropTypes.string,
    }),
  }),
  reader: PropTypes.shape({
    hostname: PropTypes.string,
    commerce: PropTypes.string,
    reader_type: PropTypes.string,
  }),
  gettingPower: PropTypes.object,
  settingPower: PropTypes.object,
};

SetPowerDialog.defaultProps = {
  antenna: null,
  reader: null,
  gettingPower: {},
  settingPower: {},
};

export default SetPowerDialog;
